import { useContext, useState } from "react";
import {
  TextField,
  Button,
  Box,
  InputAdornment,
  IconButton,
  Typography,
  Paper,
} from "@mui/material";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import { AuthContext } from "../../Auth/Context";
import { useAuthClient } from "../../hooks/useAuthClient";
import { useNavigate } from "react-router-dom";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import LockOutlined from "@mui/icons-material/LockOutlined";
import PersonOutlined from "@mui/icons-material/PersonOutlined";
import CryptoJS from "crypto-js";
import { useTranslation } from "react-i18next";
import "../../Traduccion_ES_EN/i18nMiCuenta";
import logoWex from "../../assets/Gemini_Generated_Image_onn9bjonn9bjonn9.png";
import ForgotPasswordModal from "../molecules/ForgotPasswordModal";

const LoginForm = () => {
  const { t } = useTranslation();
  const { payload } = useContext(AuthContext);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("Rellena todos los campos");
  const { loginClient } = useAuthClient();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotModal, setShowForgotModal] = useState(false);

  const onLogin = async () => {
    if (username === "" || password === "") {
      setMessage("Rellena todos los campos");
      setOpen(true);
    } else {
      try {
        const response = await loginClient(username, password);

        if (!response.success) {
          setMessage(response.message);
          setOpen(true);
        } else {
          login(response.user);
          const encryptedPassword = CryptoJS.AES.encrypt(
            password,
            "secret_key",
          ).toString();
          sessionStorage.setItem("encryptedPassword", encryptedPassword);

          if (
            response.user?.profile?.class?.trim().toUpperCase() === "_MASTER_"
          ) {
            navigate("/dashboard-master", { replace: true });
          } else if (response.user?.profile?.class === "_AGENT_") {
            navigate("/agents-home", { replace: true });
          } else if (response.user?.profile?.class === "representative") {
            navigate("/workshops", { replace: true });
          } else {
            navigate("/option-dashboard", { replace: true });
          }
        }
      } catch (err) {
        setMessage(err.message);
        setOpen(true);
      }
    }
  };

  const handleClose = (event, reason) => {
    if (reason === "clickaway") return;
    setOpen(false);
  };

  return (
    <Paper
      elevation={6}
      sx={{
        p: { xs: 2.5, sm: 4 },
        borderRadius: 2,
        width: "100%",
        maxWidth: { xs: "100%", sm: 380 },
        textAlign: "center",
        boxSizing: "border-box",
      }}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onLogin();
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
          <img
            src={logoWex}
            alt="Logo"
            style={{
              width: "80%",
              maxWidth: 280,
              height: "auto",
            }}
          />
        </Box>

        <TextField
          id="username"
          placeholder="Usuario"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          fullWidth
          margin="normal"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <PersonOutlined
                  sx={{
                    fontSize: 20,
                    color: "#bdbdbd",
                    alignSelf: "center",
                  }}
                />
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-input": {
              fontFamily: "Raleway, sans-serif",
              fontSize: { xs: "15px", sm: "18px" },
              color: "#424242",
            },
            "& input::placeholder": {
              fontFamily: "Raleway, sans-serif",
              fontSize: { xs: "15px", sm: "18px" },
            },
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: "#eeeeee",
                borderWidth: "1px",
              },
              "&:hover fieldset": {
                borderColor: "#eeeeee",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#f28500",
                borderWidth: "1.5px",
              },
            },
          }}
        />

        <TextField
          id="password"
          placeholder="Contraseña"
          type={showPassword ? "text" : "password"}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          fullWidth
          margin="normal"
          autoComplete="new-password"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockOutlined
                  sx={{
                    fontSize: 20,
                    color: "#bdbdbd",
                    alignSelf: "center",
                  }}
                />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? (
                    <Visibility
                      sx={{
                        fontSize: 20,
                        color: "#bdbdbd",
                        alignSelf: "center",
                      }}
                    />
                  ) : (
                    <VisibilityOff
                      sx={{
                        fontSize: 20,
                        color: "#bdbdbd",
                        alignSelf: "center",
                      }}
                    />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-input": {
              fontFamily: "Raleway, sans-serif",
              fontSize: "18px",
              color: "#424242",
            },
            "& input::placeholder": {
              fontFamily: "Raleway, sans-serif",
              fontSize: "18px",
            },
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: "#eeeeee",
                borderWidth: "1px",
              },
              "&:hover fieldset": {
                borderColor: "#eeeeee",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#f28500",
                borderWidth: "1.5px",
              },
            },
          }}
        />

        <Button
          fullWidth
          variant="contained"
          type="submit"
          sx={{
            mt: { xs: 2, sm: 3 },
            backgroundColor: "#1E88E5",
            borderRadius: 1,
            py: { xs: 1, sm: 1.2 },
            fontSize: { xs: "13px", sm: "15px" },
            fontFamily: "Raleway, sans-serif",
          }}
        >
          INICIAR SESIÓN
        </Button>

        <Button
          fullWidth
          variant="text"
          onClick={() => setShowForgotModal(true)}
          sx={{
            mt: { xs: 2, sm: 3 },
            backgroundColor: "#f3f5f697",
            borderRadius: 1,
            py: { xs: 1, sm: 1.2 },
            fontSize: { xs: "13px", sm: "15px" },
            fontFamily: "Raleway, sans-serif",
            color: "#000000",
          }}
        >
          {t("Welcome.text304")}
        </Button>
      </form>

      <ForgotPasswordModal
        open={showForgotModal}
        onClose={() => setShowForgotModal(false)}
      />

      <Snackbar
        open={open}
        autoHideDuration={3000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          onClose={handleClose}
          severity="error"
        >
          {message}
        </MuiAlert>
      </Snackbar>

      <Typography
        sx={{ mt: 2, fontSize: { xs: "11px", sm: "13px" }, fontWeight: 400, color: "#9e9e9e" }}
      >
        {t("Welcome.text303")}
      </Typography>
    </Paper>
  );
};

export default LoginForm;
